import React, { useReducer, useEffect, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import ai from "../../helpers/axios";
import AsyncSelect from "react-select/async";
import DatePicker from "react-date-picker";
import * as Sentry from "@sentry/react";
import "./click-to-fly-calculator.css";

import AircraftPricingCard from "./aircraft-pricing-card";
import ViewToggle from "./view-toggle";
import ClickToFlyTableView from "./click-to-fly-table-view";

/**
 * Click to Fly Calculator Content
 * Updated to use ClickToFlyResult document structure
 */
const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      let value =
        action.fieldType === "number" ? parseInt(action.value) : action.value;

      return {
        ...state,
        [action.field]: value,
      };
    }

    case "airportChange": {
      const selectedKey = action.airportType === "departureAirport" 
        ? "selectedDepartureAirport" 
        : "selectedArrivalAirport";
      return {
        ...state,
        [action.airportType]: action.payload,
        [selectedKey]: action.selectedOption || null,
      };
    }

    case "tripTypeChanged": {
      return {
        ...state,
        tripType: action.payload.tripType,
        returnDate: action.payload.tripType === 1 ? null : state.returnDate,
      };
    }

    case "estimateReturned": {
      // Use ClickToFlyResult document directly
      const clickToFlyResult = action.payload.clickToFlyResult;
      const isEligible =
        clickToFlyResult &&
        clickToFlyResult.aircraftOptions &&
        clickToFlyResult.aircraftOptions.length > 0;

      return {
        ...state,
        clickToFlyResult: clickToFlyResult,
        clickToFlyResultId: action.payload.clickToFlyResultId,
        isClickToFlyEligible: isEligible,
        estimateDoc: action.payload.estimateDoc,
        ineligibleReason: !isEligible ? "Route not eligible for Click to Fly" : null,
        isLoading: false,
      };
    }

    case "setLoading": {
      return {
        ...state,
        isLoading: action.payload,
      };
    }

    case "reset": {
      return {
        ...initialState,
      };
    }

    case "prefillForm": {
      return {
        ...state,
        departureAirport: action.payload.departureAirport,
        arrivalAirport: action.payload.arrivalAirport,
        selectedDepartureAirport: action.payload.selectedDepartureAirport,
        selectedArrivalAirport: action.payload.selectedArrivalAirport,
        passengers: action.payload.passengers,
        tripType: action.payload.tripType,
        departureDate: action.payload.departureDate,
        returnDate: null,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  departureAirport: {
    airportName: null,
    id: null,
    code: null,
  },
  arrivalAirport: {
    airportName: null,
    id: null,
    code: null,
  },
  selectedDepartureAirport: null, // For controlled AsyncSelect
  selectedArrivalAirport: null, // For controlled AsyncSelect
  passengers: 0,
  departureDate: null,
  tripType: 1,
  returnDate: null,
  clickToFlyResult: null,
  clickToFlyResultId: null,
  isClickToFlyEligible: null,
  ineligibleReason: null,
  estimateDoc: null,
  isLoading: false,
};

const ClickToFlyCalculatorContent = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [view, setView] = useState("cards"); // 'cards' or 'table'
  const [showTestTools, setShowTestTools] = useState(false);
  const resultsRef = React.useRef(null);

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const userRoles = user && user["https://new.evojets.com/roles"];
  const isSuperAdminOrAD = userRoles?.some(
    (role) => role === "Super Admin" || role === "Account Director"
  );

  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey && !e.shiftKey && !e.metaKey && e.key === ".") {
      e.preventDefault();
      setShowTestTools((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      let estimateData = {
        departureAirport: state.departureAirport,
        arrivalAirport: state.arrivalAirport,
        passengers: state.passengers,
        departureDate: state.departureDate,
        tripType: state.tripType,
        returnDate: state.returnDate,
        origin: "flightDeckInternal",
        userId: user?.sub || null,
      };

      let { data } = await ai
        .auth(token)
        .post(`/api/estimates/create`, estimateData)
        .catch((error) => {
          console.error(error);
          Sentry.captureException(error);
          throw error;
        });

      // If we have a clickToFlyResultId, fetch the full document
      let clickToFlyResult = null;
      if (data.clickToFlyResultId) {
        try {
          const ctfResponse = await ai
            .auth(token)
            .get(`/api/clickToFly/result/${data.clickToFlyResultId}`);
          clickToFlyResult = ctfResponse.data;
        } catch (ctfError) {
          console.error("Error fetching ClickToFlyResult:", ctfError);
        }
      }

      dispatch({
        type: "estimateReturned",
        payload: {
          ...data,
          clickToFlyResult: clickToFlyResult,
        },
      });
    } catch (error) {
      console.error("Error submitting estimate:", error);
      dispatch({ type: "setLoading", payload: false });
    }
  };

  const loadAirportOptions = async (inputValue) => {
    let ip =
      Math.floor(Math.random() * 255) +
      1 +
      "." +
      (Math.floor(Math.random() * 255) + 0) +
      "." +
      (Math.floor(Math.random() * 255) + 0) +
      "." +
      (Math.floor(Math.random() * 255) + 0);
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&ip_address=${ip}`);

    let options = [];
    if (data.length >= 1) {
      options = data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
      }));
    }

    return options;
  };

  useEffect(() => {
    document.title = "Click to Fly Calculator | evoJets HQ";
  }, []);

  // Scroll to results when they appear
  useEffect(() => {
    if (
      (state.clickToFlyResult || state.ineligibleReason) &&
      resultsRef.current
    ) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [state.clickToFlyResult, state.ineligibleReason]);

  const handleDepartureChange = (selectedAirport) => {
    dispatch({
      type: "airportChange",
      airportType: "departureAirport",
      payload: {
        airportName: selectedAirport?.label || "",
        id: selectedAirport?.value || "",
        code: selectedAirport?.code || "",
      },
      selectedOption: selectedAirport,
    });
  };

  const handleArrivalChange = (selectedAirport) => {
    dispatch({
      type: "airportChange",
      airportType: "arrivalAirport",
      payload: {
        airportName: selectedAirport?.label || "",
        id: selectedAirport?.value || "",
        code: selectedAirport?.code || "",
      },
      selectedOption: selectedAirport,
    });
  };

  /**
   * Prefill Demo Handler
   * Prefills the form with test data: KTEB -> KDJT, 5 passengers, one-way, 10 days from now
   * This function is self-contained for easy extraction
   */
  const handlePrefillDemo = async () => {
    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      // Generate random IP for airport search (matching loadAirportOptions pattern)
      const generateRandomIp = () =>
        `${Math.floor(Math.random() * 255) + 1}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;

      // Search for KTEB (Teterboro)
      const { data: departureResults } = await ai
        .auth(token)
        .get(`/api/airports/search?q=KTEB&ip_address=${generateRandomIp()}`);

      // Search for KDJT (Donald J. Trump Intl., formerly KPBI Palm Beach)
      const { data: arrivalResults } = await ai
        .auth(token)
        .get(`/api/airports/search?q=KDJT&ip_address=${generateRandomIp()}`);

      if (!departureResults?.length || !arrivalResults?.length) {
        console.error("Could not find airports for prefill");
        return;
      }

      const departureAirportData = departureResults[0];
      const arrivalAirportData = arrivalResults[0];

      // Create selected option objects (matching AsyncSelect format)
      const selectedDeparture = {
        value: departureAirportData._id,
        label: departureAirportData.airportName,
        code: departureAirportData.code,
      };

      const selectedArrival = {
        value: arrivalAirportData._id,
        label: arrivalAirportData.airportName,
        code: arrivalAirportData.code,
      };

      // Calculate date 10 days from now
      const departureDateValue = new Date();
      departureDateValue.setDate(departureDateValue.getDate() + 10);

      // Dispatch prefill action with all values
      dispatch({
        type: "prefillForm",
        payload: {
          departureAirport: {
            airportName: departureAirportData.airportName,
            id: departureAirportData._id,
            code: departureAirportData.code,
          },
          arrivalAirport: {
            airportName: arrivalAirportData.airportName,
            id: arrivalAirportData._id,
            code: arrivalAirportData.code,
          },
          selectedDepartureAirport: selectedDeparture,
          selectedArrivalAirport: selectedArrival,
          passengers: 5,
          tripType: 1, // One-way
          departureDate: departureDateValue,
        },
      });
    } catch (error) {
      console.error("Error prefilling form:", error);
    }
  };

  return (
    <div className="ctf-calculator-page">
      <div className="text-center hero">
        <h1 className="mb-4">Click to Fly Calculator</h1>
      </div>

      <form className="ctf-calculator-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="tripType">Trip Type</label>
          <select
            id="tripType"
            value={state.tripType}
            onChange={(e) =>
              dispatch({
                type: "tripTypeChanged",
                payload: {
                  tripType: parseInt(e.currentTarget.value),
                },
              })
            }
            name="tripType"
            required
          >
            <option key="placeholder" value={0} hidden>
              Trip Type
            </option>
            <option key="oneWay" value={1}>
              One-Way
            </option>
            <option key="roundTrip" value={2}>
              Round Trip
            </option>
          </select>
        </div>

        <div className="form-group">
          <label>Departure Airport</label>
          <AsyncSelect
            className="departure-airport-select"
            classNamePrefix="select"
            cacheOptions
            loadOptions={loadAirportOptions}
            placeholder="Search Airports"
            onChange={handleDepartureChange}
            value={state.selectedDepartureAirport}
          />
        </div>

        <div className="form-group">
          <label>Arrival Airport</label>
          <AsyncSelect
            className="arrival-airport-select"
            classNamePrefix="select"
            cacheOptions
            loadOptions={loadAirportOptions}
            placeholder="Search Airports"
            onChange={handleArrivalChange}
            value={state.selectedArrivalAirport}
          />
        </div>

        <div className="form-group">
          <label htmlFor="departureDate">Departure Date</label>
          <DatePicker
            name="departureDate"
            onChange={(date) =>
              dispatch({
                type: "fieldChanged",
                field: "departureDate",
                value: date,
              })
            }
            value={state.departureDate}
          />
        </div>

        {state.tripType === 2 && (
          <div className="form-group">
            <label htmlFor="returnDate">Return Date</label>
            <DatePicker
              name="returnDate"
              onChange={(date) =>
                dispatch({
                  type: "fieldChanged",
                  field: "returnDate",
                  value: date,
                })
              }
              value={state.returnDate}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="passengers">Passengers</label>
          <input
            id="passengers"
            type="number"
            value={state.passengers || ""}
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: "passengers",
                value: e.currentTarget.value,
                fieldType: "number",
              })
            }
            min="1"
            max="400"
            required
          />
        </div>

        {isSuperAdminOrAD && showTestTools && (
          <button
            type="button"
            className="btn prefill-demo-btn"
            onClick={handlePrefillDemo}
            style={{
              backgroundColor: "var(--darkspringgreen)",
              color: "white",
              width: "100%",
              padding: "0.75rem",
              marginBottom: "0.75rem",
              border: "none",
              borderRadius: "4px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Prefill Demo Data (KTEB → KDJT)
          </button>
        )}

        <button
          type="submit"
          className="btn btn-primary calculate-btn"
          disabled={state.isLoading}
        >
          {state.isLoading
            ? "Calculating..."
            : "Calculate Click to Fly Pricing"}
        </button>
      </form>

      {/* Results Section */}
      {state.ineligibleReason && !state.clickToFlyResult && (
        <div className="eligibility-info" ref={resultsRef}>
          <h3>Route Not Eligible for Click to Fly</h3>
          <p className="reason">{state.ineligibleReason}</p>
        </div>
      )}

      {state.clickToFlyResult && state.clickToFlyResult.aircraftOptions && (
        <div className="aircraft-results" ref={resultsRef}>
          <div className="results-header-row">
            <h2 className="results-header">
              Available Aircraft ({state.clickToFlyResult.aircraftOptions.length})
            </h2>
            <div className="results-header-actions">
              <ViewToggle currentView={view} onViewChange={setView} />
              {state.clickToFlyResultId && (
                <Link 
                  to={`/click-to-fly/results/${state.clickToFlyResultId}`}
                  className="view-results-btn"
                >
                  View Results Page
                </Link>
              )}
            </div>
          </div>

          {view === "cards" && (
            <div className="card-view-container">
              {[...state.clickToFlyResult.aircraftOptions]
                .sort((a, b) => {
                  const priceA = a.theoreticalMaxDiscount?.lowestPossiblePrice || a.defaultPrice || 0;
                  const priceB = b.theoreticalMaxDiscount?.lowestPossiblePrice || b.defaultPrice || 0;
                  return priceA - priceB;
                })
                .map((aircraft) => (
                <AircraftPricingCard
                  key={typeof aircraft.aircraftTypeId === 'object' ? aircraft.aircraftTypeId?._id : aircraft.aircraftTypeId}
                  aircraft={aircraft}
                  tripType={state.tripType}
                  departureDate={state.departureDate}
                  departureAirportCode={state.departureAirport.code}
                  arrivalAirportCode={state.arrivalAirport.code}
                />
              ))}
            </div>
          )}

          {view === "table" && (
            <ClickToFlyTableView
              clickToFlyData={state.clickToFlyResult}
              tripType={state.tripType}
              departureDate={state.departureDate}
              departureAirportCode={state.departureAirport.code}
              arrivalAirportCode={state.arrivalAirport.code}
              estimateDoc={state.estimateDoc}
            />
          )}
        </div>
      )}

      {!state.isLoading &&
        !state.clickToFlyResult &&
        !state.ineligibleReason && (
          <div className="no-results">
            <p>Submit the form to see Click to Fly pricing options</p>
          </div>
        )}
    </div>
  );
};

export default ClickToFlyCalculatorContent;
